import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  ArrowLeft, Truck, RefreshCw, ArrowRight, Container,
  CheckCircle2, Printer, User, Phone, Package,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

import { MovementStatusSelect, movementActions, recordMovement } from "@/components/ConsignmentMovement";
import { getApiErrorMessage } from "@/utils/apiError";
import { getLRDisclaimer } from "@/utils/lrDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["Open", "Confirmed", "In Transit", "Delivered", "Completed", "On Hold"];

const STATUS_CLS = {
  "Open":       "bg-blue-50 text-blue-700 border-blue-200",
  "Confirmed":  "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Pick-Up Done": "bg-blue-50 text-blue-700 border-blue-200",
  "Awaiting Movement": "bg-violet-50 text-violet-700 border-violet-200",
  "Scheduled":  "bg-violet-50 text-violet-700 border-violet-200",
  "Departed":   "bg-amber-50 text-amber-700 border-amber-200",
  "Arrived":    "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  "At Warehouse": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Out for Delivery": "bg-orange-50 text-orange-700 border-orange-200",
  "Awaiting Completion": "bg-orange-50 text-orange-700 border-orange-200",
  "Delivery Complete": "bg-green-50 text-green-700 border-green-200",
  "Complete":   "bg-green-50 text-green-700 border-green-200",
  "Completed":  "bg-green-50 text-green-700 border-green-200",
  "Delivered":  "bg-green-50 text-green-700 border-green-200",
  "On Hold":    "bg-red-50 text-red-600 border-red-200",
  "Cancelled":  "bg-slate-100 text-slate-600 border-slate-200",
};

const TYPE_LABEL = {
  from_location: "Pickup",
  warehouse:     "Warehouse",
  to_location:   "Delivery",
};

const lbl = "text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5";
const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

export default function ConsignmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRM = user?.role === "relationship_manager";

  const [consignment,     setConsignment]     = useState(null);
  const [transfers,       setTransfers]       = useState([]);
  const [selectedStatus,  setSelectedStatus]  = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [selectedLRs, setSelectedLRs] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [driverMode,      setDriverMode]      = useState("system"); // "system" | "temp"
  const [drivers,         setDrivers]         = useState([]);
  const [selectedDriver,  setSelectedDriver]  = useState("");
  const [driverForm,      setDriverForm]      = useState({ name: "", number: "" });
  const [showPrintModal,  setShowPrintModal]  = useState(false);
  const [printCustomer,   setPrintCustomer]   = useState(true);
  const [printDriver,     setPrintDriver]     = useState(false);

  const fetchAll = async () => {
    try {
      const [conRes, drvRes] = await Promise.all([
        axios.get(`${API}/trips/${id}`, { withCredentials: true }),
        axios.get(`${API}/users/drivers`,      { withCredentials: true }),
      ]);
      setConsignment(conRes.data);
      setTransfers(conRes.data.transfers || []);
      setSelectedStatus(conRes.data.status);
      setCurrentLocation(conRes.data.current_location || "");
      setDrivers(drvRes.data);
    } catch {
      toast.error("Failed to load consignment.");
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  const bulkAction = async action => {
    const selected = transfers.filter(t => selectedLRs.includes(t.id));
    if (!selected.length || !window.confirm(`Confirm ${action} for ${selected.length} selected consignments?`)) return;
    setBulkBusy(true);
    const failures = [];
    for (const t of selected) {
      if (t.movement_history?.at(-1)?.trip_id !== id || !movementActions(t).some(([a]) => a === action)) {
        failures.push(`${t.lr_number}: action unavailable`); continue;
      }
      try { await recordMovement(t, action); }
      catch (error) { failures.push(`${t.lr_number}: ${getApiErrorMessage(error)}`); }
    }
    await fetchAll(); setSelectedLRs([]); setBulkBusy(false);
    if (failures.length) toast.error(failures.join("; "));
    else toast.success("Selected consignments updated.");
  };

  /* ── Status update ───────────────────────────────────────────────────── */
  const handleStatusUpdate = () => {
    if (!selectedStatus) {
      toast.error("Please select a status.");
      return;
    }
    if (selectedStatus === consignment?.status && currentLocation === (consignment?.current_location || "")) {
      toast.error("Please change the status or current location.");
      return;
    }
    if (selectedStatus === "Confirmed") {
      setDriverMode("system");
      setSelectedDriver("");
      setDriverForm({ name: consignment.driver_name || "", number: consignment.driver_number || "" });
      setShowConfirm(true);
      return;
    }
    applyStatusUpdate(selectedStatus, { current_location: currentLocation.trim() });
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    if (driverMode === "system") {
      if (!selectedDriver) { toast.error("Please select a driver."); return; }
      const drv = drivers.find(d => d.id === selectedDriver);
      setShowConfirm(false);
      applyStatusUpdate("Confirmed", {
        driver_name:   drv.name.trim(),
        driver_number: drv.phone?.trim() || "",
      });
    } else {
      if (!driverForm.name.trim() || !driverForm.number.trim()) {
        toast.error("Driver name and number are required.");
        return;
      }
      setShowConfirm(false);
      applyStatusUpdate("Confirmed", {
        driver_name:   driverForm.name.trim(),
        driver_number: driverForm.number.trim(),
      });
    }
  };

  const applyStatusUpdate = async (status, extra = {}) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/trips/${id}`,
        { status, current_location: currentLocation.trim(), ...extra },
        { withCredentials: true }
      );
      await fetchAll();
      toast.success(`Trip-sheet status updated to "${status}".`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Print All LRs ───────────────────────────────────────────────────── */
  const executePrint = ({ customer, driver }) => {
    if (transfers.length === 0) { toast.error("No transfers to print."); return; }
    const win = window.open("", "_blank", "width=900,height=700,toolbar=no,menubar=no");
    if (!win) { toast.error("Pop-up blocked. Please allow pop-ups."); return; }

    const baseVehicleType = consignment.vehicle_type || consignment.temp_vehicle_type || "";
    const vehicleType = baseVehicleType
      ? `${baseVehicleType}${consignment.closed_vehicle ? " - Closed Vehicle" : ""}`
      : "—";

    const copyTypes = [];
    if (customer) copyTypes.push("Customer's Copy");
    if (driver)   copyTypes.push("Driver's Copy");

    const logoUrl = `${window.location.origin}/city_connect_logo.png`;
    const logoHtml = `<img src="${logoUrl}" alt="City Connect" class="brand-logo" />`;

    const allPages = [];

    transfers.forEach(t => {
      const transferCopies = [];
      const tDate = t.transfer_date
        ? new Date(t.transfer_date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : "—";
      const quantityHtml = (t.load_details || []).map(ld =>
        `<li>${ld.load_name || "Load"}${ld.handle_with_care ? " <span style='color:#B45309;font-weight:900'>⚠ HWC</span>" : ""}</li>`
      ).join("") || "<li>—</li>";
      const disclaimer = getLRDisclaimer(t.load_details || []);

      const totalWeight = (t.load_details || []).reduce((sum, ld) => ld.weight ? sum + parseFloat(ld.weight) : sum, 0);
      const weightStr = totalWeight > 0 ? `${totalWeight} kg` : "NA";
      const rawToPayAmount = t.to_pay_amount || t.amount_at_delivery || "";
      const hasToPay = Number(rawToPayAmount) > 0;
      const toPayStr = hasToPay ? "YES" : "NO";
      const toPayAmount = hasToPay ? rawToPayAmount : "—";
      const ewayStr          = t.eway_bill_no != null && t.eway_bill_no !== "" ? t.eway_bill_no : "NA";
      const valueStr         = t.value_of_consignment != null && t.value_of_consignment !== "" ? t.value_of_consignment : "NA";
      const insStr           = t.transit_insurance ? "Yes" : "No";
      const ownerStr         = t.owner_risk ? "Yes" : "No";
      const carrierStr       = t.carrier_risk ? "Yes" : "No";

      copyTypes.forEach(copyLabel => {
        transferCopies.push(`
          <div class="lr-block">
            <!-- Header -->
            <table class="hdr-table">
              <tr>
                <td class="hdr-logo">${logoHtml}</td>
                <td class="hdr-info">
                  <div class="company-addr">Near Lakshmipuram Palace, Puzhavathu, Lakshmipuram, Changanacherry, Kottayam, Kerala - 686101</div>
                  <div class="company-addr"><strong>GSTIN: 32BFKPR7082B1ZG</strong></div>
                  <div class="company-addr">Email: cityconnectnetwork@gmail.com / Website: www.cityconnectlogistics.com</div>
                  <div class="company-addr">Phone: 80506 84433 / 70229 09521</div>
                </td>
              </tr>
            </table>

            <!-- Title -->
            <div class="lr-title">CONSIGNMENT NOTE &nbsp;-&nbsp; ${copyLabel}</div>

            <!-- LR No + Booking Date -->
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">L.R. NO :</span> <strong>${t.lr_number || "—"}</strong></td>
                <td class="half-r"><span class="k">Booking Date :</span> ${tDate}</td>
              </tr>
            </table>

            <!-- Vehicle No + Vehicle Type -->
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">Vehicle No:</span> ${consignment.vehicle_display || "—"}</td>
                <td class="half-r"><span class="k">Vehicle Type :</span> ${vehicleType}</td>
              </tr>
            </table>

            <!-- Consignor + Consignee -->
            <table class="data-table">
              <tr>
                <td class="khead">Consignor Name:</td><td class="vhalf">${t.customer_name || "—"}</td>
                <td class="khead">Consignee Name:</td><td class="vhalf">${t.customer_name || "—"}</td>
              </tr>
            </table>

            <!-- Phone x2 -->
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">Phone:</span> ${t.customer_phone || "—"}${t.alternate_number ? ` &nbsp;|&nbsp; <span class="k">Alternate:</span> ${t.alternate_number}` : ""}</td>
                <td class="half-r"><span class="k">Phone:</span> ${t.customer_phone || "—"}${t.alternate_number ? ` &nbsp;|&nbsp; <span class="k">Alternate:</span> ${t.alternate_number}` : ""}</td>
              </tr>
            </table>

            <!-- Addresses -->
            <table class="data-table">
              <tr>
                <td class="half-l addr"><span class="k">Pick Up Address:</span><br>${t.origin_label || "—"}</td>
                <td class="half-r addr"><span class="k">Drop Address:</span><br>${t.destination_label || "—"}</td>
              </tr>
            </table>

            <!-- Description + Value -->
            <table class="data-table">
              <tr>
                <td class="half-l desc"><span class="k">Description (Said to Contain):</span><p class="goods-disclaimer">${disclaimer}</p></td>
                <td class="half-r"><span class="k">Value of Consignment :</span> ${valueStr}</td>
              </tr>
            </table>

            <!-- Payment + QEW -->
            <table class="data-table">
              <tr>
                <td rowspan="2" class="freight-cell">
                  <div class="k" style="margin-bottom:4px">Payment:</div>
                  <table class="inner-table">
                    <tr>
                      <td class="k">TO PAY</td>
                      <td class="k">TO PAY AMOUNT</td>
                    </tr>
                    <tr>
                      <td><strong>${toPayStr}</strong></td>
                      <td>${toPayAmount}</td>
                    </tr>
                  </table>
                </td>
                <td class="qew-head k quantity-col">QUANTITY :</td>
                <td class="qew-head k eway-col">E-WAY BILL NO :</td>
                <td class="qew-head k weight-col">WEIGHT :</td>
              </tr>
              <tr>
                <td class="qew-val load-quantity quantity-col"><ul>${quantityHtml}</ul></td>
                <td class="qew-val eway-col">${ewayStr}</td>
                <td class="qew-val weight-col">${weightStr}</td>
              </tr>
            </table>

            <!-- Insurance + Risk -->
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">Transit Insurance Opted</span> &nbsp; ${insStr}</td>
                <td class="half-r">
                  <span class="k">Owner's Risk :</span> ${ownerStr} &nbsp;&nbsp;
                  <span class="k">Carrier's Risk :</span> ${carrierStr}
                </td>
              </tr>
            </table>

            ${consignment.driver_name ? `
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">Driver:</span> ${consignment.driver_name}</td>
                <td class="half-r"><span class="k">Driver Phone:</span> ${consignment.driver_number || "—"}</td>
              </tr>
            </table>` : ""}

            <!-- Remarks + POD -->
            <table class="data-table">
              <tr>
                <td class="half-l remarks"><span class="k">REMARKS AT THE TIME OF RECEIVING</span><br><br><br></td>
                <td class="half-r remarks">
                  <div class="k" style="text-decoration:underline">DETAILS AT THE TIME OF RECEIVING: POD</div>
                  <br>Date: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <br><br>Time:
                </td>
              </tr>
            </table>

            <!-- Booking By + Receiver Signature -->
            <table class="data-table">
              <tr>
                <td class="half-l"><span class="k">BOOKING BY:</span></td>
                <td class="half-r sig"><span class="k">RECEIVER'S SIGNATURE &amp; SEAL</span></td>
              </tr>
            </table>

            <div class="electronic-declaration">This is an electronically generated statement and does not need a signature</div>
          </div>`);
      });
      allPages.push(`<div class="lr-sheet">${transferCopies.join("")}</div>`);
    });

    win.document.write(`<!DOCTYPE html>
<html><head><title>LRs — ${consignment.consignment_number}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 9px; color: #0F172A; }
  .lr-sheet { height: 281mm; display: grid; grid-template-rows: 1fr 1fr; gap: 3mm; page-break-after: always; break-after: page; }
  .lr-sheet:last-child { page-break-after: auto; break-after: auto; }
  .lr-block { min-height: 0; padding: 3mm 4mm; border: 1.5px solid #1B2A6B; overflow: hidden; }
  .hdr-table { width: 100%; border-collapse: collapse; margin-bottom: 0; border-bottom: 1px solid #1B2A6B; }
  .hdr-logo { width: 120px; padding: 3px; vertical-align: middle; border-right: 1px solid #1B2A6B; }
  .brand-logo { display: block; width: 108px; height: auto; object-fit: contain; }
  .hdr-info { padding: 3px 6px; vertical-align: middle; text-align: left; }
  .company-addr { font-size: 7.8px; color: #475569; line-height: 1.35; }
  .lr-title { background: #F8FAFC; color: #1B2A6B; text-align: center; font-size: 12px; font-weight: 900; letter-spacing: 1.4px; padding: 4px; border-bottom: 1px solid #1B2A6B; }
  .data-table { width: 100%; border-collapse: collapse; border-bottom: 1px solid #CBD5E1; }
  .data-table td { padding: 2.5px 4px; border-right: 1px solid #CBD5E1; font-size: 8.5px; line-height: 1.25; vertical-align: top; }
  .data-table td:last-child { border-right: none; }
  .half-l { width: 50%; }
  .half-r { width: 50%; }
  .khead { font-weight: 700; background: #F8FAFC; width: 15%; white-space: nowrap; }
  .vhalf { width: 35%; }
  .k { font-weight: 700; color: #1B2A6B; }
  .addr { height: 35px; vertical-align: top; }
  .desc ul { padding-left: 10px; margin: 1px 0; }
  .desc li { font-size: 8px; }
  .goods-disclaimer { margin-top: 2px; color: #1B2A6B; font-size: 7.8px; font-weight: 700; }
  .remarks { height: 38px; vertical-align: top; }
  .sig { text-align: right; vertical-align: bottom; padding-bottom: 4px; }
  .freight-cell { width: 50%; border-right: 1px solid #CBD5E1; padding: 2px 4px; }
  .inner-table { width: 100%; border-collapse: collapse; margin-top: 1px; }
  .inner-table td { border: 1px solid #E2E8F0; padding: 1.5px 3px; font-size: 8px; }
  .qew-head { width: 17%; text-align: center; font-size: 7.8px; border-bottom: 1px solid #CBD5E1; padding: 2px 3px; }
  .qew-val  { width: 17%; text-align: center; font-size: 8px; padding: 2px 3px; }
  .quantity-col { width: 28%; }
  .eway-col { width: 12%; }
  .weight-col { width: 10%; }
  .load-quantity { text-align: left; }
  .load-quantity ul { margin: 0; padding-left: 10px; }
  .electronic-declaration { padding-top: 3px; text-align: center; color: #475569; font-size: 7.5px; font-style: italic; }
  @media print { @page { size: A4; margin: 8mm; } .lr-block { border: 1.5px solid #1B2A6B; } }
</style></head>
<body>${allPages.join("")}</body>
<script>window.onload = function(){ window.print(); }<\/script>
</html>`);
    win.document.close();
    setShowPrintModal(false);
  };
  /* ── Derived: all loads from all transfers ───────────────────────────── */
  const allLoads = transfers.flatMap(t =>
    (t.load_details || []).map(ld => ({
      ...ld,
      lr_number: t.lr_number,
      customer_name: t.customer_name,
    }))
  );

  if (!consignment) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-[#94A3B8] text-sm gap-2">
        <RefreshCw size={15} className="animate-spin" /> Loading...
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-10" data-testid="consignment-detail-page">

      {/* Back + title */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate("/trips")}
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] transition-colors"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <span className="text-[#E2E8F0]">/</span>
        <span className="font-mono font-black text-sm text-[#1B2A6B]">{consignment.consignment_number}</span>
        <span className={`ml-auto inline-flex items-center px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_CLS[consignment.status] || STATUS_CLS["Open"]}`}>
          {consignment.status}
        </span>
      </div>

      {/* Info + Status Update */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left: Details */}
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0]">
          <div className="bg-[#1B2A6B] px-6 py-4 border-l-4 border-[#FFD700]">
            <h2 className="text-lg font-bold text-white font-mono">{consignment.consignment_number}</h2>
            <p className="text-white/60 text-xs mt-0.5">
              {consignment.consignment_date
                ? format(new Date(consignment.consignment_date + "T00:00:00"), "dd MMM yyyy")
                : "—"}{" "}
              · {consignment.vehicle_display || "—"}
            </p>
          </div>
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Date</p>
              <p className="text-sm font-semibold text-[#0F172A]">
                {consignment.consignment_date
                  ? format(new Date(consignment.consignment_date + "T00:00:00"), "dd MMM yyyy")
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Vehicle</p>
              <div className="flex items-center gap-1.5">
                <Truck size={13} className="text-[#94A3B8]" />
                <p className="text-sm font-semibold text-[#0F172A]">{consignment.vehicle_display || "—"}</p>
                {consignment.is_temp_vehicle && (
                  <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5">Temp</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Consignments</p>
              <p className="text-sm font-semibold text-[#0F172A]">{transfers.length} transfer{transfers.length !== 1 ? "s" : ""}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Status</p>
              <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_CLS[consignment.status] || STATUS_CLS["Open"]}`}>
                {consignment.status}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Current Location</p>
              <p className="text-sm font-semibold text-[#0F172A]">{consignment.current_location || "—"}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Movement Leg</p>
              <p className="text-sm font-semibold text-[#0F172A]">{consignment.origin_label || "—"} → {consignment.destination_label || "—"}</p>
            </div>
            {/* Driver info — visible once confirmed */}
            {consignment.driver_name && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Driver</p>
                <div className="flex items-center gap-1.5">
                  <User size={12} className="text-[#94A3B8]" />
                  <p className="text-sm font-semibold text-[#0F172A]">{consignment.driver_name}</p>
                </div>
              </div>
            )}
            {consignment.driver_number && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Driver Phone</p>
                <div className="flex items-center gap-1.5">
                  <Phone size={12} className="text-[#94A3B8]" />
                  <p className="text-sm font-semibold text-[#0F172A]">{consignment.driver_number}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Trip Sheet status */}
        <div className="bg-white border border-[#E2E8F0] p-6 flex flex-col gap-5">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">Trip Sheet Status</h3>
            <p className="text-[11px] text-[#94A3B8] mt-1">
              {isRM ? "Read-only after LR creation." : "This status belongs to the Trip Sheet. Update each consignment's movement separately below."}
            </p>
          </div>
          {!isRM && <div className="space-y-3">
            <Input value={currentLocation} onChange={e => setCurrentLocation(e.target.value)}
              placeholder="Current godown / drop point" className={INPUT_CLS} />
            <Select value={selectedStatus} onValueChange={setSelectedStatus} data-testid="consignment-status-select">
              <SelectTrigger className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s} className="rounded-none">
                    <div className="flex items-center gap-2">
                      {s === consignment.status && <CheckCircle2 size={12} className="text-green-500" />}
                      {s}
                      {s === consignment.status && <span className="text-[10px] text-[#94A3B8]">(current)</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="update-status-btn"
              onClick={handleStatusUpdate}
              disabled={saving || (selectedStatus === consignment.status && currentLocation === (consignment.current_location || ""))}
              className="w-full rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 border-b-2 border-[#FFD700] disabled:opacity-50"
            >
              {saving ? "Updating..." : "Update Trip Sheet Status"}
            </Button>
          </div>}
          {isRM && <p className="text-sm text-[#64748B]">Current location and status are updated by operations.</p>}
        </div>
      </div>

      <section className="space-y-3">
        <div>
          <h3 className="font-semibold">Consignment Movement Updates</h3>
          <p className="mt-1 text-xs text-[#64748B]">
            These actions update each selected consignment's movement on this Trip Sheet. Arriving at a warehouse keeps it active for the next leg; after customer delivery, a Warehouse Manager can mark it Completed once no payment is pending.
          </p>
        </div>
        {!isRM && <div className="flex gap-2 flex-wrap">
          {[["depart", "Confirm Departure"], ["arrive", "Confirm Arrival"], ["deliver", "Confirm Delivery"]].map(([action, label]) =>
            <button key={action} disabled={bulkBusy || !selectedLRs.length} className="border px-3 py-2 text-xs disabled:opacity-50" onClick={() => bulkAction(action)}>{label} (selected)</button>)}
        </div>}
      </section>
      {/* Transfers */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">
            Consignments
            <span className="ml-2 text-[#94A3B8] font-normal normal-case">({transfers.length})</span>
          </h3>
          {transfers.length > 0 && (
            <button
              data-testid="print-all-lrs-btn"
              onClick={() => setShowPrintModal(true)}
              className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-3 py-1.5"
            >
              <Printer size={11} />Print All LRs
            </button>
          )}
        </div>

        {transfers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Container size={36} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No transfers assigned to this consignment yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  {(!isRM ? ["Select", "LR Number", "Customer", "This Leg: From", "This Leg: To", "Pickup Date", "Loads", "Leg Status", "Consignment State", "Update Movement"] : ["LR Number", "Customer", "This Leg: From", "This Leg: To", "Pickup Date", "Loads", "Leg Status", "Consignment State"]).map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transfers.map((t, idx) => {
                  const leg = [...(t.movement_history || [])].reverse().find(m => m.trip_id === id) || {};
                  return (
                  <tr key={t.id} className={`border-b border-[#E2E8F0] last:border-0 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}>
                    {!isRM && <td className="px-4 py-3">
                      <input aria-label={`Select ${t.lr_number}`} type="checkbox" disabled={bulkBusy} checked={selectedLRs.includes(t.id)} onChange={e => setSelectedLRs(prev => e.target.checked ? [...prev, t.id] : prev.filter(x => x !== t.id))} />
                    </td>}
                    <td className="px-4 py-3">
                      <span className="font-mono font-black text-xs text-[#1B2A6B] bg-[#EEF0FA] px-2 py-0.5 border border-[#C7D2FE] tracking-wider">
                        {t.lr_number || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[#0F172A] whitespace-nowrap">{t.customer_name || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{TYPE_LABEL[leg.origin_type] || leg.origin_type}</span>
                        <span className="text-xs text-[#0F172A]">{leg.origin_label || "Unconfirmed"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <ArrowRight size={12} className="text-[#94A3B8] shrink-0" />
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{TYPE_LABEL[leg.destination_type] || leg.destination_type}</span>
                          <span className="text-xs text-[#0F172A]">{leg.destination_label || "Unconfirmed"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">
                      {t.transfer_date ? format(new Date(t.transfer_date + "T00:00:00"), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#475569]">{(t.load_details || t.load_ids || []).length}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_CLS[leg.status] || STATUS_CLS["Open"]}`}>
                        {leg.event_version === 1 ? leg.status : "Legacy — unconfirmed"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex w-fit items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_CLS[t.status] || STATUS_CLS["Open"]}`}>
                          {t.status || "—"}
                        </span>
                        <span className="max-w-[180px] truncate text-[10px] text-[#64748B]" title={t.current_location || "Location not confirmed"}>
                          {t.current_location || "Location not confirmed"}
                        </span>
                      </div>
                    </td>
                    {!isRM && <td className="px-4 py-3">
                      <MovementStatusSelect transfer={t} tripId={id} onSaved={() => fetchAll()} />
                    </td>}
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Loads Grid */}
      {allLoads.length > 0 && (
        <div className="bg-white border border-[#E2E8F0]">
          <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E2E8F0] flex items-center gap-2">
            <Package size={13} className="text-[#475569]" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">
              All Loads
              <span className="ml-2 text-[#94A3B8] font-normal normal-case">({allLoads.length})</span>
            </h3>
          </div>
          <div className="p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5">
            {allLoads.map((ld, idx) => (
              <div
                key={idx}
                className={`border p-3 flex flex-col gap-1 bg-white relative ${ld.handle_with_care ? "border-amber-300 bg-amber-50/40" : "border-[#E2E8F0]"}`}
              >
                {ld.handle_with_care && (
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1 py-0.5 leading-none">⚠</span>
                )}
                <p className="font-mono font-black text-[11px] text-[#1B2A6B] leading-tight truncate">{ld.load_ref || "—"}</p>
                <p className="text-[11px] text-[#475569] leading-tight truncate">{ld.load_name || "—"}</p>
                {ld.customer_name && (
                  <p className="text-[10px] text-[#1B2A6B] font-semibold leading-tight truncate">{ld.customer_name}</p>
                )}
                <p className="text-[10px] text-[#94A3B8] font-mono leading-tight truncate">{ld.lr_number || ""}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm (Driver Details) Dialog */}
      <Dialog open={showConfirm} onOpenChange={o => { if (!o) setShowConfirm(false); }}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Confirm Trip</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Assign a driver to confirm {consignment.consignment_number}</p>
          </div>
          <form onSubmit={handleConfirmSubmit}>
            <div className="px-6 py-5 space-y-4">

              {/* Toggle */}
              <div className="flex border border-[#E2E8F0] rounded-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDriverMode("system")}
                  className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    driverMode === "system"
                      ? "bg-[#1B2A6B] text-white"
                      : "bg-white text-[#475569] hover:bg-[#F8FAFC]"
                  }`}
                >
                  System Driver
                </button>
                <button
                  type="button"
                  onClick={() => setDriverMode("temp")}
                  className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest transition-colors border-l border-[#E2E8F0] ${
                    driverMode === "temp"
                      ? "bg-[#1B2A6B] text-white"
                      : "bg-white text-[#475569] hover:bg-[#F8FAFC]"
                  }`}
                >
                  Temporary Driver
                </button>
              </div>

              {/* System Driver: dropdown */}
              {driverMode === "system" && (
                <div>
                  <label className={lbl}>Select Driver *</label>
                  {drivers.length === 0 ? (
                    <p className="text-xs text-[#94A3B8] italic">No active drivers found. Add users with the Driver role, or use Temporary Driver.</p>
                  ) : (
                    <Select value={selectedDriver} onValueChange={setSelectedDriver} data-testid="system-driver-select">
                      <SelectTrigger className={INPUT_CLS}>
                        <SelectValue placeholder="Choose a driver…" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {drivers.map(d => (
                          <SelectItem key={d.id} value={d.id} className="rounded-none">
                            <span className="font-semibold">{d.name}</span>
                            {d.phone && <span className="text-[#94A3B8] ml-2 text-[11px]">{d.phone}</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Temporary Driver: name + number */}
              {driverMode === "temp" && (
                <>
                  <div>
                    <label className={lbl}>Driver Name *</label>
                    <Input
                      value={driverForm.name}
                      onChange={e => setDriverForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Ramesh Kumar"
                      data-testid="driver-name-input"
                      className={INPUT_CLS}
                    />
                  </div>
                  <div>
                    <label className={lbl}>Driver Phone *</label>
                    <Input
                      value={driverForm.number}
                      onChange={e => setDriverForm(f => ({ ...f, number: e.target.value }))}
                      placeholder="e.g. 9876543210"
                      data-testid="driver-number-input"
                      className={INPUT_CLS}
                    />
                  </div>
                </>
              )}

            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="confirm-consignment-btn"
                className="rounded-none bg-indigo-600 hover:bg-indigo-700 text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-indigo-800"
              >
                Confirm &amp; Set Driver
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Print Copy Selection Modal ── */}
      <Dialog open={showPrintModal} onOpenChange={o => !o && setShowPrintModal(false)}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base flex items-center gap-2">
              <Printer size={15} /> Print LRs
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Select which copies to print</p>
          </div>
          <div className="px-6 py-5 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={printCustomer}
                onChange={e => setPrintCustomer(e.target.checked)}
                className="accent-[#1B2A6B] w-4 h-4"
              />
              <div>
                <p className="text-sm font-semibold text-[#0F172A]">Customer's Copy</p>
                <p className="text-xs text-[#64748B]">One copy per LR for the customer</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={printDriver}
                onChange={e => setPrintDriver(e.target.checked)}
                className="accent-[#1B2A6B] w-4 h-4"
              />
              <div>
                <p className="text-sm font-semibold text-[#0F172A]">Driver's Copy</p>
                <p className="text-xs text-[#64748B]">One copy per LR for the driver</p>
              </div>
            </label>
          </div>
          <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
            <button type="button" onClick={() => setShowPrintModal(false)}
              className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
              Cancel
            </button>
            <Button
              onClick={() => {
                if (!printCustomer && !printDriver) { toast.error("Select at least one copy type."); return; }
                executePrint({ customer: printCustomer, driver: printDriver });
              }}
              disabled={!printCustomer && !printDriver}
              className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-50"
            >
              <Printer size={12} className="mr-1.5" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
